import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  OnChanges,
  SimpleChanges,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
  FormGroup
} from '@angular/forms';
import find from 'lodash-es/find';
import { map, skip, tap } from 'rxjs/operators';

import {
  IFBCustomForm,
  IFBCustomFormField,
  IFBCustomFormFieldResponse,
  IFBCustomFormResponse
} from '@app/models/custom-form';
import { formGroupValidator } from '@app/modules/ui/validators/form-group-validator';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { CompanyService } from '@app/shared/services/company.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-custom-form',
  templateUrl: './custom-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomFormComponent),
      multi: true
    }
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomFormComponent implements OnChanges, ControlValueAccessor, Validator, AfterViewInit {
  private company = inject(CompanyService);
  private translate = inject(TranslateService);

  form = input<IFBCustomForm>();
  showErrors = input<boolean>();
  updatedValidity = output<boolean>();

  isLoaded = signal(false);
  fields = signal<IFBCustomFormField[]>([]);
  locale = signal<string>('');
  response: IFBCustomFormResponse;

  onTouched: () => void;
  onChange: (value: any) => void;

  responseForm = new FormGroup(
    {},
    {
      // Group level validator to announce custom form validation status to parent
      // (required when run by angularjs in hybrid mode)
      validators: formGroupValidator(this.updatedValidity)
    }
  );

  constructor() {
    this.responseForm.valueChanges
      .pipe(
        // Watch form changes, but return formatted response data
        map(() => ({ ...this.response })),
        skip(1),
        takeUntilDestroyed()
      )
      .subscribe(res => {
        if (this.onTouched && this.onChange) {
          this.onTouched();
          this.onChange(res);
        }
      });
  }

  ngAfterViewInit() {
    // Prevents NG0100 error in development more
    requestAnimationFrame(() => {
      // Pushed model updates from inside of the component to parent control
      this.responseForm.updateValueAndValidity();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form && changes.form.currentValue) {
      const form = changes.form.currentValue;

      this.locale.set(this.defineLocale(form));

      this.fields.set(form.fields.filter(field => field.fieldType !== 'picture-upload'));

      this.fields().forEach(field => {
        const validators = field.mandatory ? Validators.requiredTrue : undefined;
        this.responseForm.addControl(field.id, new UntypedFormControl(false, validators));
      });

      this.response = {
        form: { id: form.id },
        locale: this.locale(),
        responses: this.fields().map(field => ({ id: field.id, response: false })),
        version: form.version
      };

      this.isLoaded.set(true);
    }

    if (changes.showErrors && changes.showErrors.currentValue) {
      for (const key in this.responseForm.controls) {
        if (this.responseForm.controls.hasOwnProperty(key)) {
          this.responseForm.controls[key].markAsDirty();
        }
      }
    }
  }

  writeValue(value: string): void {
    // Don't expect value from the parent component
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl) {
    return this.responseForm.errors;
  }

  defineLocale(form: IFBCustomForm): string {
    const companyLocale = this.company.data.languages.defaultLocale;
    const userLocale = this.translate.getLocale();

    const field = form.fields[0];
    const attr = field?.attributes[0];

    if (!attr || !attr.localizations || !attr.localizations.length) {
      // No translations available
      return '';
    }

    const localizations = attr.localizations;

    // Try to use language which user currently using
    const inUserLocale = find(localizations, { locale: userLocale });

    if (inUserLocale) {
      return userLocale;
    }

    // Try to fallback to browser language
    const inCompanyLocale = find(localizations, { locale: companyLocale });

    if (inCompanyLocale) {
      return companyLocale;
    }

    // Fallback to first localization available
    return localizations[0].locale;
  }

  propagateFieldUpdate(newValue: IFBCustomFormFieldResponse): void {
    const oldValue = find(this.response.responses, { id: newValue.id });
    if (oldValue) {
      oldValue.response = newValue.response;
    }
  }
}
